import { JSX, useMemo } from "react";

import { Alert, Box, Grid, Link, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useLocationFilesQuery, useLocationsDataQuery } from "src/api/locationQueries.api";
import FilesGrid from "src/components/Files/components/FilesGrid/FilesGrid";
import { getFilesDataGridColumns } from "src/components/Files/components/FilesGrid/filesGrid.helper";
import FilesPageBreadcrumbsComponent from "src/components/Files/components/FilesPageBreadcrumbsComponent";
import { getDataItemsForLocationId } from "src/components/Files/helper/files.helper";
import { type DataItem } from "src/components/Files/interfaces/DataItem";
import { APP_BASENAME } from "src/lib/config.helper";

const styles = {
  container: { px: { xs: 1, md: 0 } },
  fullWidth: { xs: 12 },
};

function FilesListPage(): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const { locationId } = useParams();
  const navigate = useNavigate();

  const { data: locations } = useLocationsDataQuery();
  const { data: files, isLoading, error } = useLocationFilesQuery(locationId);

  const dataItems: DataItem[] = useMemo(
    () => getDataItemsForLocationId(locations?.value ?? [], locationId, files ?? []),
    [files, locationId, locations?.value]
  );

  const rootLocation = useMemo(() => locations?.value.find((item) => item.LocalName === "/"), [locations]);
  const selectedLocation = useMemo(() => locations?.value.find((item) => item.Id === locationId), [locationId, locations?.value]);
  const columns = useMemo(() => getFilesDataGridColumns(t, navigate, locationId, theme), [t, navigate, locationId, theme]);

  return (
    <Grid container spacing={2}>
      <Grid size={styles.fullWidth}>
        <Box sx={styles.container}>
          <FilesPageBreadcrumbsComponent selectedFileName={undefined} selectedLocation={selectedLocation} rootLocation={rootLocation} />
        </Box>
      </Grid>
      <Grid size={styles.fullWidth}>
        {!selectedLocation && error && (
          <Alert severity='warning'>
            {t("This folder does not exist, go back to")}{" "}
            <Link href={`${APP_BASENAME}/files`} color='inherit'>
              {t("home")}
            </Link>
          </Alert>
        )}
        {selectedLocation && <FilesGrid data={dataItems} columns={columns} isLoading={isLoading} error={error} />}
      </Grid>
    </Grid>
  );
}

export default FilesListPage;
